import "../../styles/bootstrap/bootstrap-popover.scss";
import "../../styles/popoverOverride.scss";
import "./formList.scss";

import { findIndex, isNil } from "lodash";
import PropTypes from "prop-types";
import React, { createRef, Fragment } from "react";
import { PopoverBody } from "reactstrap";

import NavigationToolbar from "../../components/navigationToolbar";
import { withScreenContext } from "../../components/screen/ScreenContext";
import KeyConstants from "../../constants/keyConstants";
import { isScreenModeMobile } from "../../constants/screenModes";
import { PropTypeShapes } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import { withContainerContext } from "../container/ContainerContext";
import Icon from "../interface/Icon";
import { popoverOrPortal } from "../portal/popoverOrPortal";
import FormListButtonWrapper from "./FormListButtonWrapper";
import FormSearchField from "./FormSearchField";
import { getFilteredOptions } from "./utils";

const SAFE_SPACE = "__SPACE__";
const SAFE_NULL = "__NULL__";

class FormList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      popoverOpen: false,
      searchText: "",
      highlightValue: null
    };

    this.toggle = this.toggle.bind(this);
    this.onDownArrow = this.onDownArrow.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.searchChanged = this.searchChanged.bind(this);

    this._searchRef = createRef();
  }

  toggle() {
    this.setState({
      popoverOpen: !this.state.popoverOpen,
      searchText: ""
    });
    if (this.props.onToggleMenu) {
      this.props.onToggleMenu(!this.state.popoverOpen);
    }
  }

  optionClick(value) {
    const wasSelected = Boolean(this.props.values.find(v => v.value === value));
    if (this.props.closeOnClick) {
      this.toggle();
    }
    if (!wasSelected) {
      this.props.onAddItem(value);
    } else {
      this.props.onDeleteItem(value);
    }
  }

  searchChanged(searchText) {
    this.setState({ searchText, highlightValue: null });
  }

  onDownArrow() {
    this._listViewDom ? this._listViewDom.focus() : null;
    this.moveHighlight(1);
  }

  onKeyDown(e) {
    // Is it already open...?

    let keyHandled = false;

    if (this.state.popoverOpen) {
      switch (e.key) {
        case KeyConstants.Escape:
          this.hideMenu();
          keyHandled = true;
          break;
        case KeyConstants.Tab: {
          if (this.state.popoverOpen) {
            this.toggle();
          }
          keyHandled = true;
          break;
        }
        case KeyConstants.Enter: {
          if (this.state.highlightValue) {
            this.selectHighlightedOption();
          }
          keyHandled = true;
          break;
        }
        case KeyConstants.Up:
          this.moveHighlight(-1);
          keyHandled = true;
          break;
        case KeyConstants.Down:
          this.moveHighlight(1);
          keyHandled = true;
          break;
        case KeyConstants.Left:
        case KeyConstants.Right:
          keyHandled = true;
          break;
        default:
          this._searchRef && this._searchRef.current ? this._searchRef.current.focus() : null;
          break;
      }
    } else {
      // Not currently open.
      // So open it with "enter"
      switch (e.key) {
        case KeyConstants.Enter:
          this.toggle();
          keyHandled = true;
          break;
        default:
          break;
      }
    }

    if (keyHandled) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  onKeyPress(_e) {
    // TODO: When the user types a key, we should jump directly to that character...?
  }

  onBlur(_e) {}

  selectHighlightedOption() {
    if (this.state.popoverOpen && this.state.highlightValue) {
      this.optionClick(this.state.highlightValue);
    }
  }

  moveHighlight(delta) {
    const filteredOptions = getFilteredOptions(this.props.options, this.state.searchText);
    // If there are no options, do nothing
    if (filteredOptions.length === 0) {
      return;
    }
    let highlightIndex = findIndex(filteredOptions, opt => opt.value === this.state.highlightValue);
    if (isNil(highlightIndex)) {
      highlightIndex = -1;
    }

    highlightIndex += delta;
    if (highlightIndex < 0) {
      highlightIndex = 0;
    }
    if (highlightIndex >= filteredOptions.length) {
      highlightIndex = filteredOptions.length - 1;
    }
    let highlightValue = filteredOptions[highlightIndex].value;
    this.setState({ highlightValue });
    this.scrollToElement(filteredOptions[highlightIndex]);
  }

  getListItemDomId(opt) {
    const val = opt.value ? opt.value.replace(/\s/g, SAFE_SPACE) : SAFE_NULL;
    return `${this._dropdownId}-${val}`;
  }

  scrollToElement(val) {
    let domId = null;
    const options = getFilteredOptions(this.props.options, this.state.searchText);
    if (val && val.value && options.findIndex(o => o.value === val.value) >= 0) {
      domId = this.getListItemDomId(val);
    }
    const domNode = document.getElementById(domId);
    if (domNode) {
      domNode.scrollIntoViewIfNeeded ? domNode.scrollIntoViewIfNeeded() : domNode.scrollIntoView();
    }
  }

  renderOptionIcon(opt) {
    if (opt.icon) {
      return (
        <div className="formListIcon">
          <svg className={`icon ${opt.color ? opt.color : "mono"}`}>
            <use xlinkHref={`#${opt.icon}`} />
          </svg>
        </div>
      );
    } else if (opt.color) {
      return <div className={`formListColor ${opt.color}`} />;
    } else {
      return null;
    }
  }

  renderMenuOptions() {
    let style = {};
    //#!if mobile === true
// style.paddingLeft = Math.max(this.props.paddingLeft, 5);
// style.paddingRight = Math.max(this.props.paddingRight, 5);
    //#!endif

    const filteredOptions = getFilteredOptions(this.props.options, this.state.searchText);

    return filteredOptions.map(opt => {
      const selected = this.props.values.findIndex(v => v.value === opt.value) >= 0;
      const optClass = `formListMenuOption ${selected ? "selected" : ""} ${
        opt.value === this.state.highlightValue ? "highlighted" : ""
      }`;
      return (
        <div
          className={optClass}
          id={this.getListItemDomId(opt)}
          key={opt.value}
          style={style}
          onClick={() => this.optionClick(opt.value)}
        >
          <div className="formListTick">
            <svg className="icon mono">
              <use xlinkHref={selected ? "#tick" : "#blank"} />
            </svg>
          </div>
          {this.renderOptionIcon(opt)}
          <div className="formListLabel">{getLabelWithUntitled(opt.label)}</div>
        </div>
      );
    });
  }

  renderMenuSearch() {
    if (this.props.allowSearch) {
      return (
        <FormSearchField
          //#!if iOS !== true
          autoFocus={true}
          //#!endif
          ref={this._searchRef}
          placeholder="Filter"
          value={this.state.searchText}
          onChange={this.searchChanged}
          onDownArrow={this.onDownArrow}
          continuouslyUpdates={true}
          showSearchIcon={true}
          showClearIcon={true}
        />
      );
    }
    return null;
  }

  renderMenu() {
    return (
      <div>
        <div
          className={`formListMenu ${this.props.allowSearch ? "" : "noSearch"}`}
          tabIndex={0}
          ref={domNode => (this._listViewDom = domNode)}
          onKeyDown={this.onKeyDown}
          onBlur={this.onBlur}
        >
          {this.renderMenuSearch()}
          <div className="formListMenuOptions">{this.renderMenuOptions()}</div>
        </div>
      </div>
    );
  }

  renderVisibleList() {
    return this.props.values.map(listItem => (
      <div key={listItem.value} className="formListItem">
        {listItem.leftIcon ? (
          <Icon
            className="formListLeftIcon"
            icon={listItem.leftIcon}
            color={listItem.leftIconColor ? listItem.leftIconColor : "iconMono"}
          />
        ) : listItem.badgeCount ? (
          <span className={`badge ${listItem.badgeColor}`}>{listItem.badgeCount}</span>
        ) : null}
        <div className="formListItemContent">
          {listItem.label ? (
            <span
              className={`formListItemLabel ${this.props.onItemClick ? "clickable" : ""}`}
              onClick={this.props.onItemClick ? () => this.props.onItemClick(listItem.value) : null}
            >
              {listItem.label}
            </span>
          ) : null}
          {listItem.subLabel ? <span className="formListItemSubLabel">{listItem.subLabel}</span> : null}
        </div>
        {this.props.canDelete ? (
          <Icon icon="listDelete" color="iconMono" onClick={() => this.props.onDeleteItem(listItem.value)} />
        ) : null}
      </div>
    ));
  }

  renderEmptyLabel() {
    return this.props.values.length === 0 && this.props.emptyLabel ? (
      <div className="formListEmptyLabel">{this.props.emptyLabel}</div>
    ) : null;
  }

  renderAdd() {
    return this.props.canAdd ? (
      <div>
        <FormListButtonWrapper className="formListAddWrapper" onClick={this.toggle}>
          <Icon icon="listAdd" color="iconMono" id={this.props.id} />
          {this.renderEmptyLabel()}
        </FormListButtonWrapper>
      </div>
    ) : null;
  }

  renderBackdrop() {
    return this.props.canAdd && this.state.popoverOpen ? (
      <div className="formListBackdrop" onClick={this.toggle} />
    ) : null;
  }

  renderPopover() {
    let placement = "bottom-start";
    let hideArrow = false;
    let alwaysPortal = false;
    //#!if tablet === true
// placement = "right-start";
    //#!endif

    let popoverOptions = {
      className: `inspectorPopover`,
      placement,
      hideArrow,
      isOpen: this.props.canAdd && this.state.popoverOpen,
      target: this.props.id,
      toggle: this.toggle,
      boundariesElement: "viewport"
    };

    const child = (
      <Fragment>
        {this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode) ? (
          <NavigationToolbar
            title={this.props.title}
            showBack={true}
            showBackArrow={true}
            onBackClick={this.toggle}
            backText={this.props.backText ? this.props.backText : "Back"}
            flexLeft={1}
            flexCenter={3}
            flexRight={1}
          />
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>{this.renderMenu()}</PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  render() {
    return (
      <div className="formListWrapper">
        {this.renderVisibleList()}
        {this.renderAdd()}
        {this.renderBackdrop()}
        {this.state.popoverOpen && this.props.canAdd ? this.renderPopover() : null}
      </div>
    );
  }
}

FormList.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypeShapes.listOptionArray,
  values: PropTypeShapes.formListValueArray.isRequired,
  emptyLabel: PropTypes.string,
  onDeleteItem: PropTypes.func,
  onAddItem: PropTypes.func,
  onItemClick: PropTypes.func,
  disabled: PropTypes.bool,
  canAdd: PropTypes.bool,
  canDelete: PropTypes.bool,
  allowSearch: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  title: PropTypes.string,
  backText: PropTypes.string,
  onToggleMenu: PropTypes.func,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object,
// 
  // From withContainerContext
// paddingLeft: PropTypes.number,
// paddingRight: PropTypes.number
  //#!endif

  //#!endif
};

let exportedView = FormList;
//#!if iOS === true
// exportedView = withContainerContext(withScreenContext(FormList));
//#!endif
export default exportedView;
